/**
 * Registry of every app in the platform. They are one web app, Thananan OS, at one address: each app under its own
 * path (`/tms`, `/accounting`…), with its own seed colour; everything else (shell, auth, permissions, the installed
 * app, push) is shared. Add a new app here first.
 */
export type AppId = 'portal' | 'tms' | 'accounting' | 'hr' | 'payroll' | 'admin' | 'company' | 'driver' | 'drive'

export type AppIcon = 'home' | 'truck' | 'receipt' | 'users' | 'shield' | 'wallet' | 'building' | 'wheel' | 'cloud'

export interface AppInfo {
  id: AppId
  /** The product name, in English in every language: login page and browser title (the installed app is Thananan OS's) */
  name: string
  /** In the app switcher and menus, and Thananan OS's under its icon on a phone's home screen (about 12 characters fit) */
  shortName: string
  /**
   * Where it lives inside Thananan OS: `tms` → https://os.example.co.th/tms. Thananan OS's own pages are หน้าแรก's (`h`:
   * `/h/clock`, with หน้าแรก's menu beside them on a computer), but for those at the root (`rootPages`)
   */
  path: string
  /** Seed colour for the app's tonal palette */
  seed: string
  icon: AppIcon
  /**
   * One's own things only, whatever one may see elsewhere (Thananan OS): its lists ask for `mine`,
   * and work done for others (overviews, recording for someone) lives in the work apps
   */
  personal?: boolean
}

export const apps: Record<AppId, AppInfo> = {
  portal: {
    id: 'portal',
    name: 'Thananan OS',
    shortName: 'Thananan OS',
    path: 'h',
    seed: '#2D5BD7',
    icon: 'home',
    personal: true,
  },
  tms: { id: 'tms', name: 'Thananan TMS', shortName: 'TMS', path: 'tms', seed: '#00695F', icon: 'truck' },
  accounting: {
    id: 'accounting',
    name: 'Thananan Accounting',
    shortName: 'Accounting',
    path: 'accounting',
    seed: '#3F51B5',
    icon: 'receipt',
  },
  hr: { id: 'hr', name: 'Thananan HR', shortName: 'HR', path: 'hr', seed: '#B14A7A', icon: 'users' },
  admin: { id: 'admin', name: 'Thananan Admin', shortName: 'Admin', path: 'admin', seed: '#6D4FC2', icon: 'shield' },
  // Last, so the icon sheet keeps the others where they are (scripts/app-icons.mjs draws them in this order)
  payroll: {
    id: 'payroll',
    name: 'Thananan Payroll',
    shortName: 'Payroll',
    path: 'payroll',
    seed: '#2E7D32',
    icon: 'wallet',
  },
  /** What the whole company shares: its people, branches and details */
  company: {
    id: 'company',
    name: 'Thananan Company',
    shortName: 'Company',
    path: 'company',
    seed: '#D35400',
    icon: 'building',
  },
  /**
   * The driver's day on the phone: today's jobs or delivery run, clocking in, history with trip pay, and the money bag
   * (`crewApps`). One's own things only, as Thananan OS: the money bag is the crew's own there too
   */
  driver: {
    id: 'driver',
    name: 'Thananan Driver',
    shortName: 'Driver',
    path: 'driver',
    seed: '#C98A00',
    icon: 'wheel',
    personal: true,
  },
  /** Everyone's files, in folders shared as a whole (`everyoneApps`) */
  drive: { id: 'drive', name: 'Thananan Drive', shortName: 'Drive', path: 'drive', seed: '#0288D1', icon: 'cloud' },
}

/** Apps that hold business data and therefore carry permissions. */
export const businessApps = ['tms', 'accounting', 'hr', 'payroll', 'admin', 'company'] as const satisfies readonly AppId[]

/**
 * Apps for one kind of work that hold no data and carry no permissions of their own: they show other apps' data (the
 * driver app, TMS's jobs and runs), each by its own permissions, and open by who someone is, not by a role
 * (`opensApp` in `@tnn/domain`: the driver app for an account linked to a truck's crew). Nobody asks for one.
 */
export const crewApps = ['driver'] as const satisfies readonly AppId[]

/**
 * Apps everyone on the staff opens (`opensApp` in `@tnn/domain`: an account linked to an employee, or a super admin),
 * holding things that are each person's and shared by them, not by a role (Drive: one's folders). No permissions of their
 * own, nobody asks for one.
 */
export const everyoneApps = ['drive'] as const satisfies readonly AppId[]

/**
 * Modules that belong to no single app (cash in hand is a person's, whichever app took the money:
 * Thananan OS and Accounting show it, not TMS; people are invited and approved from HR, a branch
 * lead's too, and from Admin). Their resources and permissions sit beside the apps', and give no app by themselves.
 */
export type ModuleId = 'cash' | 'access'
export const sharedModules = ['cash', 'access'] as const satisfies readonly ModuleId[]

/** Where a resource belongs: its app, or a shared module */
export type ResourceHome = AppId | ModuleId

/** The groups of the permission matrix: the business apps, then the shared modules */
export const permissionGroups = [...businessApps, ...sharedModules] as const

/** Thananan OS's subdomain in production (`os.thananan.co`), and its port in `pnpm dev` */
export const platformSubdomain = 'os'
export const platformDevPort = 5170

/**
 * Sites of their own outside Thananan OS, one page each for someone who does not sign in: its subdomain beside My
 * Thananan's in production (`sign.thananan.co`), and its port in `pnpm dev`
 */
export const sites = {
  /** Where customers sign documents (apps/sign) */
  sign: { subdomain: 'sign', devPort: 5180 },
  /** A person's portable screen (apps/screen), an app of its own to install on a tablet or phone */
  screen: { subdomain: 'screen', devPort: 5181 },
} as const
export type SiteId = keyof typeof sites

export interface AppUrlOptions {
  /** Root domain in production (e.g. `thananan.co.th`). Empty → `pnpm dev` on localhost. */
  domain?: string
}

/** The one origin every app is served from: https://os.<domain>, or the dev server */
export function platformOrigin({ domain }: AppUrlOptions = {}): string {
  return domain ? `https://${platformSubdomain}.${domain}` : `http://localhost:${platformDevPort}`
}

/** An app's base path inside Thananan OS: `/tms`; `/h` for Thananan OS's own pages (หน้าแรก's) */
export const appBase = (id: AppId) => `/${apps[id].path}`

/**
 * Thananan OS's pages at the root, outside หน้าแรก's (`/h`): หน้าแรก itself (`/`), แชท, รายชื่อ, the company's apps (each a
 * page of its own, no หน้าแรก menu beside it), and those before signing in (a Drive link for outsiders too). apps/portal/src/app.tsx routes them
 * (`rootRoutes`, `publicRoutes`)
 */
export const rootPages = ['', 'chat', 'contacts', 'apps', 'login', 'join', 'signup', 'display', 'connect', 'share']

/**
 * Where an app's page is in Thananan OS, by its path from the app's own root: `/waybills` in TMS → `/tms/waybills`, `/` →
 * `/tms`, `/?scan=1` → `/tms?scan=1`; one already under it stays. Thananan OS's go under `/h` (`/clock` → `/h/clock`) but
 * for those at the root (`rootPages`: `/`, `/chat/1`) and another app's (`/tms/waybills`), which stay as they are
 */
export function appPath(id: AppId, path = '/'): string {
  const to = path || '/'
  const base = appBase(id)
  if (to === base || [`${base}/`, `${base}?`, `${base}#`].some((under) => to.startsWith(under))) return to
  if (id === 'portal' && (rootPages.includes(to.split(/[/?#]/)[1] ?? '') || appOfPath(to) !== 'portal')) return to
  if (to === '/') return base
  return /^\/[?#]/.test(to) ? base + to.slice(1) : base + to
}

/** An app's page by its full address (`appPath`): https://os.<domain>/tms, https://os.<domain>/h/clock */
export function appUrl(id: AppId, options: AppUrlOptions = {}, path = '/'): string {
  return `${platformOrigin(options)}${appPath(id, path)}`
}

/** The app a path inside Thananan OS (its query too) belongs to: the business app whose base it starts with, else Thananan OS */
export function appOfPath(path: string): AppId {
  const first = path.split(/[/?#]/)[1] ?? ''
  return Object.values(apps).find((app) => app.path && app.path === first)?.id ?? 'portal'
}
